"use client"

import { useState, useEffect } from "react"
import { Link } from "react-router-dom";
import slide1 from '../images/slide1.jpg';
import slide2 from '../images/slide2.jpg';
import slide3 from '../images/slide3.jpg';
import slide4 from '../images/slide4.jpg';
import slide5 from '../images/slide5.jpg';
import slide6 from '../images/slide6.webp';
import g4res from '../images/projects/G+4 Residential Building - Chennai.jpeg';
import mall from '../images/projects/Commercial Mall - Coimbatore.jpeg';
import warehouse from '../images/projects/Industrial Steel Warehouse - Trichy.jpeg';
import apartment from '../images/projects/High-Rise Apartment Complex - Banglore.jpeg';
import shopping from '../images/projects/Shopping Complex - Madurai.jpeg';
import manufacturing from '../images/projects/Manufacturing Plant - Salem.jpeg';


import { ChevronDown, Building, Factory, Home, Phone, MapPin } from "lucide-react"

const projects = [
  {
    id: 1,
    title: "G+4 Residential Building – Chennai",
    description:
      "Full RCC and steel structural analysis using ETABS and AutoCAD. Site supervision and final drawing certification included.",
    image: g4res,
    alt: "G+4 Residential Building in Chennai",
    category: "Residential",
    location: "Chennai",
    year: "2024",
  },
  {
    id: 2,
    title: "Commercial Mall – Coimbatore",
    description:
      "Multi-level commercial project involving Tekla-based steel design, load optimization, and seismic analysis.",
    image: mall,
    alt: "Commercial Mall in Coimbatore",
    category: "Commercial",
    location: "Coimbatore",
    year: "2023",
  },
  {
    id: 3,
    title: "Industrial Steel Warehouse – Trichy",
    description:
      "Pre-engineered steel structure modeled and analyzed using STAAD.Pro with detailed structural drawings and fabrication layout.",
    image: warehouse,
    alt: "Industrial Steel Warehouse in Trichy",
    category: "Industrial",
    location: "Trichy",
    year: "2024",
  },
  {
    id: 4,
    title: "High-Rise Apartment – Bangalore",
    description:
      "20-story residential tower with advanced seismic design and wind load analysis using ETABS and SAFE foundation design.",
    image: apartment,
    alt: "High-Rise Apartment Complex in Bangalore",
    category: "Residential",
    location: "Bangalore",
    year: "2023",
  },
  {
    id: 5,
    title: "Shopping Complex – Madurai",
    description:
      "Multi-story retail complex with parking facilities, involving comprehensive structural design and MEP coordination.",
    image: shopping,
    alt: "Shopping Complex in Madurai",
    category: "Commercial",
    location: "Madurai",
    year: "2024",
  },
  {
    id: 6,
    title: "Manufacturing Plant – Salem",
    description:
      "Large-scale industrial facility with heavy machinery foundations, crane runway beams, and specialized structural systems.",
    image: manufacturing,
    alt: "Manufacturing Plant in Salem",
    category: "Industrial",
    location: "Salem",
    year: "2023",
  },
]

const slides = [
  slide1,
  slide2,
  slide3,
  slide4,
  slide5,
  slide6,
]

export default function ProjectsPage() {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState("All")

  const categories = ["All", "Residential", "Commercial", "Industrial"]

  const filteredProjects =
    selectedCategory === "All" ? projects : projects.filter((project) => project.category === selectedCategory)

  const getCategoryIcon = (category) => {
    switch (category) {
      case "Residential":
        return <Home className="w-5 h-5" />
      case "Commercial":
        return <Building className="w-5 h-5" />
      case "Industrial":
        return <Factory className="w-5 h-5" />
      default:
        return <Building className="w-5 h-5" />
    }
  }

  // Slideshow auto-advance
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length)
    }, 4000)

    return () => clearInterval(interval)
  }, [slides.length])

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Content animation observer
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("visible")
        }
      })
    }, observerOptions)

    const contentElements = document.querySelectorAll(".content-animate")
    contentElements.forEach((el) => observer.observe(el))

    return () => observer.disconnect()
  }, [])

  return (
    <>
      <style jsx>{`
        /* Gradient animation for logo */
        @keyframes gradient-x {
          0%,
          100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        .animate-gradient-x {
          animation: gradient-x 3s ease infinite;
        }

        /* Same animations and effects from the original HTML */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        /* Hero section with slideshow background */
        .hero-section {
          position: relative;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 6rem 0 4rem 0;
          min-height: 60vh;
        }

        /* Slideshow background styles */
        .slideshow-container {
          position: absolute;
          inset: 0;
        }

        .slide {
          position: absolute;
          inset: 0;
          transition: opacity 1s ease-in-out;
          opacity: 0;
        }

        .slide.active {
          opacity: 0.3;
        }

        .slide img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        /* Overlay gradients */
        .slideshow-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(15, 118, 110, 0.2) 0%, transparent 50%, rgba(124, 58, 237, 0.2) 100%);
        }

        .slideshow-overlay-2 {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(15, 118, 110, 0.3) 0%, transparent 50%, rgba(15, 118, 110, 0.1) 100%);
        }

        /* Animated background elements */
        .bg-element-1 {
          position: absolute;
          top: 80px;
          left: 40px;
          width: 288px;
          height: 288px;
          background: linear-gradient(90deg, rgba(6, 182, 212, 0.2), rgba(139, 92, 246, 0.1));
          border-radius: 50%;
          filter: blur(48px);
          animation: pulse 4s infinite;
        }

        .bg-element-2 {
          position: absolute;
          bottom: 80px;
          right: 40px;
          width: 384px;
          height: 384px;
          background: linear-gradient(270deg, rgba(124, 58, 237, 0.2), rgba(6, 182, 212, 0.1));
          border-radius: 50%;
          filter: blur(48px);
          animation: pulse 4s infinite;
          animation-delay: 1s;
        }

        .bg-element-3 {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 800px;
          height: 800px;
          background: radial-gradient(circle, transparent 0%, rgba(41, 1, 1, 0.05) 50%, transparent 100%);
          border-radius: 50%;
          animation: spin-slow 20s linear infinite;
          pointer-events: none;
        }

        /* Title animations */
        .title-gradient {
          background: linear-gradient(120deg, #0f766e 0%, #7c3aed 50%, #0f766e 100%);
          background-size: 200% 100%;
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: gradient-x 4s ease infinite;
          position: relative;
        }

        .title-glow {
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, rgba(6, 182, 212, 0.2), rgba(59, 130, 246, 0.2));
          filter: blur(24px);
          animation: pulse 3s infinite;
        }

        /* Content animations */
        .content-animate {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease;
        }

        .content-animate.visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* Footer animations */
        .footer-link {
          padding: 8px 16px;
          font-size: 14px;
          color: #d1d5db;
          transition: all 0.3s ease;
          border: 1px solid #4b5563;
          border-radius: 8px;
          position: relative;
          overflow: hidden;
          transform: translateY(0);
          text-decoration: none;
        }

        .footer-link:hover {
          color: white;
          background: linear-gradient(90deg, #06b6d4, #7c3aed);
          border-color: #06b6d4;
          box-shadow: 0 0 15px rgba(6, 182, 212, 0.5);
          transform: scale(1.05) translateY(-4px);
        }

        .footer-link::before {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, rgba(6, 182, 212, 0.2), rgba(124, 58, 237, 0.2));
          opacity: 0;
          transition: opacity 0.3s ease;
          z-index: -1;
        }

        .footer-link:hover::before {
          opacity: 1;
        }

        .footer-link::after {
          content: "";
          position: absolute;
          bottom: 0;
          left: 0;
          width: 0;
          height: 2px;
          background: #06b6d4;
          transition: width 0.3s ease;
        }

        .footer-link:hover::after {
          width: 100%;
        }

        .footer-link span {
          position: relative;
          z-index: 10;
        }

        /* Fade in animations */
        .fade-in-element {
          opacity: 0;
          animation: fadeInUp 0.8s ease-out forwards;
        }

        .fade-in-delay-1 {
          animation-delay: 0.2s;
        }

        .fade-in-delay-2 {
          animation-delay: 0.4s;
        }

        .fade-in-delay-3 {
          animation-delay: 0.6s;
        }

        .fade-in-delay-4 {
          animation-delay: 0.8s;
        }

        /* Project card animations */
        .project-card {
          opacity: 0;
          transform: translateY(40px);
          transition: all 0.6s ease;
        }

        .project-card.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .project-card:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
        }
      `}</style>

      <div className="bg-white text-gray-800">
        {/* Navbar */}
        <header
          className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
            isScrolled
              ? "bg-white/20 backdrop-blur-2xl border-b border-white/10 shadow-xl"
              : "bg-gray-900/30 backdrop-blur-xl text-white border-b border-white/5"
          }`}
        >
          <div className="container mx-auto flex justify-between items-center px-6 py-4">
            <div
              className={`text-2xl font-bold transition-all duration-500 ${isScrolled ? "text-[#06b6d4]" : "text-white"}`}
            >
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-[#00d4ff] via-[#5b21b6] to-[#ec4899] bg-clip-text text-transparent animate-gradient-x bg-[length:200%_100%]">
                  StructuralGuru
                </span>
                <div className="absolute -inset-3 bg-gradient-to-r from-[#00d4ff]/20 via-[#5b21b6]/20 to-[#ec4899]/20 rounded-2xl blur-2xl opacity-50 animate-pulse"></div>
                <div
                  className="absolute -inset-4 bg-gradient-to-r from-[#00d4ff]/15 via-[#5b21b6]/15 to-[#ec4899]/15 rounded-3xl blur-3xl opacity-30 animate-pulse"
                  style={{ animationDelay: "1s" }}
                ></div>
              </span>
            </div>
            <nav className="hidden md:flex space-x-8">
                {[
                    { name: "HOME", to: "/" },
                    { name: "ABOUT US", to: "/about" },
                    { name: "SERVICES", to: "/services" },
                    { name: "OUR ENGG PROJECTS", to: "/projects", active: true },
                ].map(({ name, to, active }) => (
                    <Link
                    key={name}
                    to={to}
                    className={`relative font-medium transition-all duration-300 hover:scale-105 ${
                        isScrolled ? "text-gray-700 hover:text-[#0f766e]" : "text-white hover:text-gray-200"
                    } ${
                        isScrolled
                        ? "hover:shadow-[0_0_15px_rgba(6,182,212,0.5)] hover:border hover:border-[#06b6d4]/50"
                        : "hover:shadow-[0_0_15px_rgba(255,255,255,0.4)] hover:border hover:border-white/50"
                    } rounded-lg px-2 py-1 overflow-hidden after:content-[''] after:absolute after:w-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-current after:transition-all after:duration-300 hover:after:w-full ${
                        active ? "font-bold" : ""
                    }`}
                    >
                  {name}
                </Link>
              ))}

              <div className="relative">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className={`flex items-center space-x-1 font-medium transition-all duration-300 hover:scale-105 ${
                    isScrolled ? "text-gray-700 hover:text-[#0f766e]" : "text-white hover:text-gray-200"
                  } ${
                    isScrolled
                      ? "hover:shadow-[0_0_15px_rgba(6,182,212,0.5)] hover:border hover:border-[#06b6d4]/50"
                      : "hover:shadow-[0_0_15px_rgba(255,255,255,0.4)] hover:border hover:border-white/50"
                  } rounded-lg px-2 py-1`}
                >
                  <span>MORE</span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-300 ${isDropdownOpen ? "rotate-180" : ""}`}
                  />
                </button>
                <div
                  className={`absolute top-full right-0 mt-2 w-48 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/20 overflow-hidden transition-all duration-300 ${
                    isDropdownOpen ? "opacity-100 translate-y-0 visible" : "opacity-0 -translate-y-4 invisible"
                  }`}
                >
                  <Link
                    to="/privacy"
                    className="block px-6 py-3 text-gray-700 hover:bg-gray-50 hover:text-[#0f766e] transition-all duration-200"
                  >
                    Privacy Policy
                  </Link>
                  <Link
                    to="/quality"
                    className="block px-6 py-3 text-gray-700 hover:bg-gray-50 hover:text-[#0f766e] transition-all duration-200"
                  >
                    Quality Policy
                  </Link>
                </div>
              </div>
            </nav>
          </div>
        </header>

        {/* Hero Section with Slideshow Background */}
        <section className="hero-section text-center">
          {/* Slideshow Background */}
          <div className="slideshow-container">
            {slides.map((slide, index) => (
              <div key={index} className={`slide ${index === currentSlide ? "active" : ""}`}>
                <img
                  src={slide || "/placeholder.svg"}
                  alt={`Construction Site ${index + 1}`}
                  fill
                  className="object-cover"
                />
              </div>
            ))}

            {/* Overlay gradients */}
            <div className="slideshow-overlay"></div>
            <div className="slideshow-overlay-2"></div>
          </div>

          {/* Animated background elements */}
          <div className="bg-element-1"></div>
          <div className="bg-element-2"></div>
          <div className="bg-element-3"></div>

          {/* Content */}
          <div className="max-w-4xl mx-auto relative z-10 py-8 px-6">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 fade-in-element fade-in-delay-1 relative">
              <span className="title-gradient">Our Engineering Projects</span>
              <div className="title-glow"></div>
            </h1>
            <p className="text-xl mb-8 content-animate fade-in-delay-2 text-gray-800 drop-shadow-md max-w-3xl mx-auto leading-relaxed">
              Showcasing excellence in structural engineering across residential, commercial, and industrial sectors
              with innovative solutions and cutting-edge technology.
            </p>
          </div>
        </section>

        {/* Filter Section */}
        <section className="py-8 bg-white/80 backdrop-blur-sm border-b content-animate">
          <div className="container mx-auto px-6">
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 ${
                    selectedCategory === category
                      ? "bg-gradient-to-r from-[#0f766e] to-[#7c3aed] text-white shadow-lg"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {category !== "All" && getCategoryIcon(category)}
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Projects Grid */}
        <section className="py-16 px-6 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {filteredProjects.map((project, index) => (
                <div
                  key={project.id}
                  className="project-card content-animate bg-white rounded-2xl shadow-lg overflow-hidden"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={project.image || "/placeholder.svg"}
                      alt={project.alt}
                      fill
                      className="object-cover transition-transform duration-500 hover:scale-110"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-gradient-to-r from-[#0f766e] to-[#7c3aed] text-white px-3 py-1 rounded-full text-sm font-semibold">
                        {project.category}
                      </span>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                      <MapPin className="w-4 h-4" />
                      {project.location}
                      <span className="ml-auto bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-semibold">
                        {project.year}
                      </span>
                    </div>

                    <h3 className="text-xl font-bold text-[#0f766e] mb-3 hover:text-[#7c3aed] transition-colors duration-300">
                      {project.title}
                    </h3>

                    <p className="text-gray-600 leading-relaxed mb-4">{project.description}</p>

                    <button className="w-full bg-gradient-to-r from-[#0f766e] to-[#7c3aed] text-white py-3 rounded-lg font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300">
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-[#0f766e] to-[#7c3aed] text-white content-animate">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold mb-6 fade-in-element">Ready to Start Your Project?</h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto fade-in-element fade-in-delay-1">
              Let our expert team bring your structural engineering vision to life with precision and excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center fade-in-element fade-in-delay-2">
              <Link
                to="/contact"
                className="bg-yellow-400 text-[#0f766e] px-8 py-4 rounded-lg font-bold hover:bg-yellow-300 transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Get Quote
              </Link>
              <Link
                to="/services"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold hover:bg-white hover:text-[#0f766e] transform hover:scale-105 transition-all duration-300"
              >
                Our Services
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white relative overflow-hidden">
          <div className="relative z-10 py-12">
            <div className="container mx-auto px-6">
              {/* Main Footer Navigation */}
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <Link to="/" className="footer-link">
                  <span>Home</span>
                </Link>
                <Link to="/psdct" className="footer-link">
                  <span>Psdct Live Mode</span>
                </Link>
                <Link to="/terms" className="footer-link">
                  <span>Term & Conditions</span>
                </Link>
                <Link to="/quick-payment" className="footer-link">
                  <span>Quick Pay Any Fees</span>
                </Link>
                <Link to="/registration-policy" className="footer-link">
                  <span>Registration Policy</span>
                </Link>
                <Link to="/cancel" className="footer-link">
                  <span>Cancellation Policy</span>
                </Link>
                <Link to="/careers" className="footer-link">
                  <span>Careers</span>
                </Link>
                <Link to="/contact" className="footer-link">
                  <span>Contact Us</span>
                </Link>
                <Link to="/franchise" className="footer-link">
                  <span>Franchise</span>
                </Link>
                <Link to="/free" className="footer-link">
                  <span>Free</span>
                </Link>
                <Link to="/students" className="footer-link">
                  <span>Students</span>
                </Link>
                <Link to="/training" className="footer-link">
                  <span>Training Centers</span>
                </Link>
              </div>

              {/* Bottom Section */}
              <div className="border-t border-gray-700 pt-8">
                <div className="text-center fade-in-element">
                  <p className="text-sm text-gray-400">
                    &copy; 2025 StructuralGuru - GURURAYAR ASSOCIATES. All Rights Reserved.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  )
}
